import React, { useState, useMemo } from 'react'
import { Card, Button, Badge, EmptyState } from './ui'
import { TELESALES_AGENTS } from '../lib/constants'
import { Plus, Trash2, Pencil, Save, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { todayWIB } from './MyWorkToday'

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return d.toLocaleDateString('en-CA')
}
const prettyDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const norm = (s) => (s || '').toString().trim().toLowerCase()

// Personal daily journal. Agent writes/edits their own notes per date.
// Superadmin can read everyone (agent filter) but writes are attributed to them.
export default function Diary({
  diary = [], currentAgent = '', isSuperadmin = false,
  onAdd, onUpdate, onDelete,
}) {
  const [date, setDate] = useState(todayWIB())
  const [filterAgent, setFilterAgent] = useState('')   // superadmin only
  const [draft, setDraft] = useState('')
  const [editId, setEditId] = useState(null)
  const [editBody, setEditBody] = useState('')
  const [busy, setBusy] = useState(false)

  const entries = useMemo(() => {
    let list = diary.filter(e => e.entry_date === date)
    if (isSuperadmin) {
      if (filterAgent) list = list.filter(e => norm(e.agent) === norm(filterAgent))
    } else {
      list = list.filter(e => norm(e.agent) === norm(currentAgent))
    }
    return list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
  }, [diary, date, filterAgent, isSuperadmin, currentAgent])

  const save = async () => {
    const body = draft.trim()
    if (!body || !onAdd) return
    setBusy(true)
    try {
      await onAdd({ agent: currentAgent, entry_date: date, body })
      setDraft('')
    } finally { setBusy(false) }
  }
  const startEdit = (e) => { setEditId(e.id); setEditBody(e.body || '') }
  const saveEdit = async () => {
    const body = editBody.trim()
    if (!body || !onUpdate) { setEditId(null); return }
    await onUpdate(editId, { body, updated_at: new Date().toISOString() })
    setEditId(null); setEditBody('')
  }
  const canModify = (e) => isSuperadmin || norm(e.agent) === norm(currentAgent)

  return (
    <div>
      {/* Date nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => setDate(addDays(date, -1))} title="Hari sebelumnya"
          style={navBtn}><ChevronLeft size={16} /></button>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111', minWidth: 210, textAlign: 'center' }}>{prettyDate(date)}</div>
        <button onClick={() => setDate(addDays(date, 1))} title="Hari berikutnya"
          style={navBtn}><ChevronRight size={16} /></button>
        <button onClick={() => setDate(todayWIB())} style={{ ...navBtn, width: 'auto', padding: '0 10px', fontSize: 12, fontWeight: 600 }}>Hari ini</button>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ fontSize: 12, padding: '6px 9px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8 }} />
        {isSuperadmin && (
          <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)}
            style={{ marginLeft: 'auto', fontSize: 12, padding: '6px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontWeight: 600 }}>
            <option value="">Semua agent</option>
            {TELESALES_AGENTS.map(a => <option key={a}>{a}</option>)}
          </select>
        )}
      </div>

      {/* New entry composer (not for superadmin-as-viewer; still allowed to add own note) */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Catatan untuk {prettyDate(date)}</div>
        <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3}
          placeholder="Tulis catatan harian… (rencana, hasil, kendala, follow-up penting)"
          style={textareaStyle} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="primary" size="sm" onClick={save} disabled={busy || !draft.trim()}>
            <Plus size={13} /> Simpan catatan
          </Button>
        </div>
      </Card>

      {/* Entries */}
      {entries.length === 0 ? (
        <EmptyState>Belum ada catatan untuk tanggal ini.</EmptyState>
      ) : entries.map(e => (
        <Card key={e.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Badge variant="info">{e.agent}</Badge>
            <span style={{ fontSize: 11, color: '#aaa' }}>
              {e.created_at ? new Date(e.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' }) : ''}
              {e.updated_at && e.updated_at !== e.created_at ? ' · diedit' : ''}
            </span>
            {canModify(e) && (
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {editId === e.id ? (
                  <>
                    <button onClick={saveEdit} title="Simpan" style={iconBtn}><Save size={14} /></button>
                    <button onClick={() => setEditId(null)} title="Batal" style={iconBtn}><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(e)} title="Edit" style={iconBtn}><Pencil size={14} /></button>
                    <button onClick={() => onDelete && onDelete(e.id)} title="Hapus" style={{ ...iconBtn, color: '#E24B4A' }}><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            )}
          </div>
          {editId === e.id ? (
            <textarea value={editBody} onChange={ev => setEditBody(ev.target.value)} rows={3} style={textareaStyle} />
          ) : (
            <div style={{ fontSize: 13, color: '#222', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{e.body}</div>
          )}
        </Card>
      ))}
    </div>
  )
}

const navBtn = { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, cursor: 'pointer', color: '#333' }
const iconBtn = { background: 'none', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '4px 6px', cursor: 'pointer', color: '#555', display: 'flex', alignItems: 'center' }
const textareaStyle = { width: '100%', fontSize: 13, padding: '9px 11px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }
